import { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Space, Popconfirm, message, Upload, Image, Switch, Row, Col, Divider, Tag } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined,
  QrcodeOutlined, MenuOutlined, PhoneOutlined, EnvironmentOutlined,
  InstagramOutlined, GlobalOutlined, ShopOutlined, LinkOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import API from '../api/client';
import { BACKEND_URL } from '../../config';

const BusinessesPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try { const res = await API.get('/businesses'); setData(res.data); }
    catch { message.error('Veriler yüklenemedi'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setImageUrl('');
    form.resetFields();
    form.setFieldsValue({ isRestaurant: false });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record._id);
    form.resetFields();
    // Set after reset to avoid stale data
    setTimeout(() => {
      setImageUrl(record.image || '');
      form.setFieldsValue({
        name: record.name || '',
        description: record.description || '',
        image: record.image || '',
        phone: record.phone || '',
        location: record.location || '',
        instagram: record.instagram || '',
        website: record.website || '',
        isRestaurant: record.isRestaurant || false,
        instagramMenuHandle: record.instagramMenuHandle || '',
        slug: record.slug || '',
      });
    }, 0);
    setModalOpen(true);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await API.post('/upload', formData);
      const url = `${BACKEND_URL}${res.data.url}`;
      setImageUrl(url);
      form.setFieldsValue({ image: url });
      message.success('Görsel yüklendi');
    } catch {
      message.error('Görsel yüklenemedi');
    }
    setUploading(false);
    return false;
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await API.put(`/businesses/${editingId}`, values);
        message.success('Güncellendi');
      } else {
        await API.post('/businesses', values);
        message.success('Eklendi');
      }
      setModalOpen(false);
      fetchData();
    } catch { /* validation */ }
  };

  const handleDelete = async (id) => {
    await API.delete(`/businesses/${id}`);
    message.success('Silindi');
    fetchData();
  };

  const columns = [
    {
      title: 'Görsel', dataIndex: 'image', key: 'image', width: 80,
      render: (url) => url ? <Image src={url} width={60} height={40} style={{ objectFit: 'cover', borderRadius: 6 }} /> : '-',
    },
    {
      title: 'İşletme Adı', dataIndex: 'name', key: 'name',
      render: (name, record) => (
        <div>
          <span style={{ fontWeight: 600 }}>{name}</span>
          {record.isRestaurant && <Tag color="orange" style={{ marginLeft: 8, fontSize: 10 }}>Restoran</Tag>}
          {record.instagramMenuHandle && <Tag color="purple" style={{ marginLeft: 4, fontSize: 10 }}>IG Menü</Tag>}
        </div>
      ),
    },
    { title: 'Telefon', dataIndex: 'phone', key: 'phone', responsive: ['md'] },
    {
      title: 'Slug', dataIndex: 'slug', key: 'slug', responsive: ['lg'],
      render: (slug) => slug ? <code style={{ fontSize: 11 }}>/menu/{slug}</code> : '-',
    },
    {
      title: 'İşlem', key: 'actions', width: 200,
      render: (_, record) => (
        <Space size={4}>
          {record.isRestaurant && (
            <>
              <Button icon={<MenuOutlined />} size="small" onClick={() => navigate(`/admin/menu/${record._id}`)} title="Menü Yönetimi" />
              <Button icon={<QrcodeOutlined />} size="small" style={{ color: '#2F2386' }} onClick={() => navigate(`/admin/qr-generator/${record._id}`)} title="QR Kod" />
            </>
          )}
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title="Silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record._id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}><ShopOutlined style={{ marginRight: 8, color: '#D4A843' }} />İşletme Yönetimi</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni İşletme</Button>
      </div>

      <Table columns={columns} dataSource={data} rowKey="_id" loading={loading} />

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'linear-gradient(135deg, #2F2386, #4a3aaa)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ShopOutlined style={{ color: '#D4A843', fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>{editingId ? 'İşletme Düzenle' : 'Yeni İşletme'}</div>
              <div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>İşletme bilgilerini doldurun</div>
            </div>
          </div>
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        width={800}
        okText="Kaydet"
        cancelText="İptal"
        centered
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto', padding: '16px 24px' } }}
      >
        <Form form={form} layout="vertical" size="middle">
          {/* ── Temel Bilgiler ── */}
          <Divider orientation="left" orientationMargin={0} style={{ fontSize: 13, color: '#2F2386', margin: '8px 0 16px' }}>
            Temel Bilgiler
          </Divider>

          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item name="name" label="İşletme Adı" rules={[{ required: true, message: 'İşletme adı gerekli' }]}>
                <Input placeholder="Ör: Mavi Mandalya" prefix={<ShopOutlined style={{ color: '#bbb' }} />} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="phone" label="Telefon">
                <Input placeholder="0546 664 16 02" prefix={<PhoneOutlined style={{ color: '#bbb' }} />} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Açıklama">
            <Input.TextArea rows={2} placeholder="İşletme hakkında kısa açıklama..." />
          </Form.Item>

          {/* Görsel */}
          <Form.Item name="image" label="İşletme Görseli" rules={[{ required: true, message: 'Görsel gerekli' }]}>
            <Input style={{ display: 'none' }} />
          </Form.Item>
          <div style={{ marginBottom: 20, marginTop: -12, display: 'flex', alignItems: 'center', gap: 16 }}>
            {imageUrl ? (
              <Image src={imageUrl} width={120} height={80} style={{ borderRadius: 10, objectFit: 'cover' }} />
            ) : (
              <div style={{
                width: 120, height: 80, borderRadius: 10, border: '2px dashed #d9d9d9',
                display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa',
              }}>
                <UploadOutlined style={{ fontSize: 24, color: '#bbb' }} />
              </div>
            )}
            <Upload showUploadList={false} beforeUpload={handleUpload} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploading}>
                {imageUrl ? 'Görseli Değiştir' : 'Görsel Yükle'}
              </Button>
            </Upload>
          </div>

          {/* ── İletişim & Linkler ── */}
          <Divider orientation="left" orientationMargin={0} style={{ fontSize: 13, color: '#2F2386', margin: '8px 0 16px' }}>
            İletişim & Linkler
          </Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="location" label="Konum Linki">
                <Input placeholder="Google Maps linki" prefix={<EnvironmentOutlined style={{ color: '#bbb' }} />} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="instagram" label="Instagram Linki">
                <Input placeholder="https://instagram.com/..." prefix={<InstagramOutlined style={{ color: '#bbb' }} />} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="website" label="Web Sitesi">
            <Input placeholder="https://..." prefix={<GlobalOutlined style={{ color: '#bbb' }} />} />
          </Form.Item>

          {/* ── Restoran Ayarları ── */}
          <Divider orientation="left" orientationMargin={0} style={{ fontSize: 13, color: '#2F2386', margin: '8px 0 16px' }}>
            Restoran & Menü Ayarları
          </Divider>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="isRestaurant" label="Restoran İşletmesi" valuePropName="checked">
                <Switch checkedChildren="Evet" unCheckedChildren="Hayır" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item name="slug" label="Slug (URL)" extra="Menü sayfası: /menu/{slug}">
                <Input placeholder="Otomatik oluşturulur" prefix={<LinkOutlined style={{ color: '#bbb' }} />} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="instagramMenuHandle" label="Instagram Menü Hesabı"
            extra="Bu alan doldurulursa normal menü yerine bu hesabın son Instagram postları menü olarak gösterilir (Kent Lokantası vb.)">
            <Input placeholder="@didimkentlokantasi" prefix={<InstagramOutlined style={{ color: '#C13584' }} />} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BusinessesPage;
